// What a loaded sim says about itself, shaped for the Methods caption and sheet: its methods text,
// its model, every input with its own provenance, and its validation. Nothing here is invented;
// a field the header lacks is left out.

import type { CellsHeader, CommonHeader, SimInput } from './types.ts';

export const PROVENANCE_LABEL: Record<string, string> = {
  measured: 'Measured',
  derived: 'Derived',
  reference: 'Reference',
  simulated: 'Simulated',
  illustrative: 'Illustrative',
};

export interface MethodsInput {
  what: string;
  provenance: string | null;
  source?: string;
  method?: string;
}

export interface SimMethods {
  /** One line for the caption under the viewport. */
  caption: string;
  /** The header's own Methods text. */
  text: string;
  /** What the output is: 'Simulated', or for a stand-in its header's label (usually 'Illustrative'). */
  output: string;
  standIn: boolean;
  model: { name: string; version?: string; citation?: string; url?: string; equations: string[] } | null;
  /** What the model leaves out, in the header's words (pharma: its biological scope and binding assumptions). */
  scope: string[];
  inputs: MethodsInput[];
  validation: { summary?: string; report?: string; reference?: string; rows?: ValidationRow[] } | null;
  run: { id: string; path: string; kind: string; created?: string; space?: string; pack?: string };
}

/** One model-against-reference comparison from a header's validation table. */
export interface ValidationRow {
  label: string;
  model: number;
  reference: number;
  unit: string;
  ratio: number;
}

const UNIT_SUFFIX: Record<string, string> = { ml_min: 'mL/min', ml_s: 'mL/s', mmhg: 'mmHg', cm_s: 'cm/s', m_s: 'm/s', ml: 'mL', pct: '%', s: 's', ms: 'ms' };

/**
 * Rows of `validation.rows` that compare a model value with a reference one: `model_<unit>` against
 * `ref_<unit>` (openBF's `model_ml_min`/`ref_ml_min`), labelled by vessel, name or segment.
 */
export function validationRows(rows: unknown): ValidationRow[] {
  if (!Array.isArray(rows)) return [];
  const out: ValidationRow[] = [];
  for (const r of rows) {
    if (!r || typeof r !== 'object') continue;
    const row = r as Record<string, unknown>;
    const key = Object.keys(row).find((k) => k.startsWith('model_') && typeof row[k] === 'number' && typeof row[`ref_${k.slice(6)}`] === 'number');
    if (!key) continue;
    const suffix = key.slice(6);
    const model = row[key] as number;
    const reference = row[`ref_${suffix}`] as number;
    const label = [row.vessel, row.name, row.segment, row.metric].find((v): v is string => typeof v === 'string') ?? suffix;
    const ratio = typeof row.ratio === 'number' ? row.ratio : reference ? model / reference : NaN;
    out.push({ label, model, reference, unit: UNIT_SUFFIX[suffix] ?? suffix.replace(/_/g, '/'), ratio });
  }
  return out;
}

/** 'equilibrium-site-binding' → 'Equilibrium site binding'; names with spaces stay as written. */
export function prettyName(s: string): string {
  // Spaced text is already prose; a name with digits is an identifier (ba-pial-0d-v0) and reads best as written.
  if (/\s/.test(s) || !/[-_]/.test(s) || /\d/.test(s)) return s;
  const words = s.split(/[-_]+/).filter(Boolean);
  return words.map((w, i) => (i === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w)).join(' ');
}

function equations(e: unknown): string[] {
  if (!e) return [];
  if (typeof e === 'string') return [e];
  if (Array.isArray(e)) return e.filter((x): x is string => typeof x === 'string');
  if (typeof e === 'object') return Object.entries(e as Record<string, unknown>).filter(([, v]) => typeof v === 'string').map(([k, v]) => `${k}: ${String(v)}`);
  return [];
}

const text = (v: unknown): string[] => (typeof v === 'string' && v.trim() ? [v.trim()] : Array.isArray(v) ? v.flatMap(text) : []);
const field = (o: unknown, key: string): unknown => (o && typeof o === 'object' ? (o as Record<string, unknown>)[key] : undefined);

/**
 * The header's own statements of what the output does and doesn't claim. A pharma run says it in
 * `model.spatial_engagement.biological_scope` (no efficacy or neural gain is inferred), its binding
 * assumptions, and where its PK course is extrapolated.
 */
function scope(h: CommonHeader): string[] {
  const m = h.model;
  return [...text(field(field(m, 'spatial_engagement'), 'biological_scope')), ...text(field(m, 'binding_assumptions')), ...text(field(field(m, 'reconstruction'), 'late_tail'))];
}

/** The model's equations, then (pharma) how a frame's cortex map is built from them. */
function modelEquations(h: CommonHeader): string[] {
  return [...equations(h.model?.equations), ...text(field(field(h.model, 'spatial_engagement'), 'equation'))];
}

function input(i: SimInput): MethodsInput {
  const what = i.what ?? ([i.role, i.name].filter(Boolean).join(': ') || 'input');
  return { what, provenance: i.provenance ?? null, ...(i.source ? { source: i.source } : {}), ...(i.method ? { method: i.method } : {}) };
}

const shortSha = (v?: string) => (v && /^[0-9a-f]{12,40}$/i.test(v) ? v.slice(0, 7) : v);

function validation(v: CommonHeader['validation'], report: string | undefined): SimMethods['validation'] {
  if (!v) return null;
  const rows = validationRows(v.rows);
  const reference = typeof v.reference === 'string' ? v.reference : undefined;
  if (!v.summary && !report && !rows.length && !reference) return null;
  return { ...(v.summary ? { summary: v.summary } : {}), ...(report ? { report } : {}), ...(reference ? { reference } : {}), ...(rows.length ? { rows } : {}) };
}

/** 'NEURON 9.0.2', from a cells model's `simulator`. */
function simulatorName(m: CommonHeader['model']): string | null {
  const sim = m?.simulator;
  if (typeof sim === 'string') return sim;
  if (!sim || typeof sim !== 'object') return null;
  const { name, version } = sim as { name?: unknown; version?: unknown };
  return typeof name === 'string' ? [name, typeof version === 'string' ? version : null].filter(Boolean).join(' ') : null;
}

/**
 * A model's name for the caption and sheet: without its own version at the end, which the sheet shows
 * beside it ('oral-one-compartment-apparent-v0' → 'Oral one compartment apparent'). An identifier that
 * stays one keeps its version (ba-pial-0d-v0).
 */
export function modelLabel(name: string, version?: unknown): string {
  const bare = typeof version === 'string' && version && name.endsWith(`-${version}`) ? name.slice(0, -version.length - 1) : name;
  const pretty = prettyName(bare);
  return pretty === bare ? name : pretty;
}

export function simMethods(h: CommonHeader, path: string, standIn: boolean): SimMethods {
  const modelName = h.model?.name ? modelLabel(h.model.name, h.model.version) : null;
  const label = h.scenario?.label;
  const title = typeof h.name === 'string' ? h.name : label;
  // A cells run's scenario label is a sentence of what happens; its caption is the model and the simulator that ran it.
  const cells = h.format === 'ba-cells-sim-v0';
  // The simulator once: a model named with it ("Yao L2/3 + Guet-McCreight L5, NEURON/LFPy") isn't captioned "… · NEURON/LFPy" again.
  const sim = simulatorName(h.model);
  const simulator = sim && modelName?.toLowerCase().includes(sim.toLowerCase()) ? null : sim;
  const caption = (typeof h.name === 'string' ? [title, modelName] : cells ? [modelName, simulator] : [modelName, label]).filter(Boolean).join(' · ') || h.id;
  const report = h.validation?.report ?? undefined;
  // Where a cell sits in the brain is part of what's on screen, so it's listed with the inputs, with its own provenance.
  const placement = cells ? (h as CellsHeader).placement : null;
  const placed: MethodsInput[] = placement ? [{ what: `placement in ${placement.space}`, provenance: placement.provenance ?? null, ...(placement.method ? { method: placement.method } : {}) }] : [];
  return {
    caption,
    text: h.methods ?? '',
    output: PROVENANCE_LABEL[h.provenance ?? ''] ?? (h.provenance ? prettyName(h.provenance) : 'Not stated'),
    standIn,
    model: h.model
      ? {
          name: modelName ?? h.model.id ?? 'Model',
          ...(h.model.version ? { version: shortSha(h.model.version) } : {}),
          ...(h.model.citation ? { citation: h.model.citation } : {}),
          ...(h.model.url ? { url: h.model.url } : {}),
          equations: modelEquations(h),
        }
      : null,
    scope: scope(h),
    inputs: [...(h.inputs ?? []).map(input), ...placed],
    validation: validation(h.validation, report),
    run: { id: h.id, path, kind: h.kind, ...(h.created ? { created: h.created } : {}), ...(h.space ? { space: h.space } : {}), ...(h.pack?.id ? { pack: `${h.pack.id}${h.pack.version ? ` v${h.pack.version}` : ''}` } : {}) },
  };
}
